import { useEffect, useMemo, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import { Autocomplete, CircularProgress, debounce } from '@mui/material';
import { Building } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type {
  LotSelectFragment,
  SearchLotsQuery,
} from '@graphql/__generated__/graphql';
import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import type { QuickFilterProps } from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import {
  AutocompletePaper,
  AutocompleteTextField,
  useFilterAutocompleteOpen,
} from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { useSchema } from '@modules/_shared/components/filters/useSchema';
import {
  LotSelectItem,
  SEARCH_LOTS,
} from '@modules/_shared/components/LotsSelect';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';

const GET_DEFAULT_LISTINGS = gql(/* GraphQL */ `
  query GetListingsByIds($ids: [uuid!]!) {
    lots(where: { id: { _in: $ids } }) {
      id
      ...LotSelect
    }
  }
`);

interface ListingMultiSelectProps {
  onClose?: () => void;
  onChange: (value: string[]) => void;
  defaultValue: string[];
}

export const ListingMultiSelect = ({
  onChange,
  onClose,
  defaultValue,
}: ListingMultiSelectProps) => {
  const { t } = useLocale();
  const open = useFilterAutocompleteOpen();
  const [options, setOptions] = useState<LotSelectFragment[]>([]);
  const [searchListings, { loading }] = useLazyQuery<SearchLotsQuery>(
    SEARCH_LOTS,
    { notifyOnNetworkStatusChange: true },
  );
  const [value, setValue] = useState<LotSelectFragment[]>([]);

  const { data: defaultListings } = useQuery(GET_DEFAULT_LISTINGS, {
    skip: !defaultValue,
    variables: { ids: defaultValue },
  });

  useEffect(() => {
    if (defaultListings?.lots) {
      setValue(defaultListings.lots);
    }
  }, [defaultListings]);

  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        searchListings({
          variables: {
            q: value,
          },
        }).then(response => {
          const newOptions = (response.data?.search_lots_tsv ?? []).filter(
            lot => !defaultValue.includes(lot.id),
          );
          setOptions(newOptions);
        });
      }, 275),
    [searchListings, defaultValue],
  );

  const handleSearch = (value: string) => {
    if (value) {
      debouncedSearch(value);
    } else {
      setOptions([]);
    }
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      openOnFocus
      open={open}
      disablePortal
      size="small"
      clearOnBlur={false}
      value={value}
      loading={loading}
      loadingText={t('Loading...')}
      onClose={onClose}
      sx={{ width: 350 }}
      options={[...value, ...options]}
      getOptionLabel={option => formatAddress(option.property, ', ')}
      filterOptions={x => x}
      onInputChange={(_, value) => handleSearch(value)}
      onChange={(_, values) => {
        onChange(values.map(val => val.id));
      }}
      renderInput={params => (
        <AutocompleteTextField
          {...params}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option, { selected }) => (
        <LotSelectItem lot={option} props={props} selected={selected} />
      )}
      slots={{
        paper: AutocompletePaper,
      }}
    />
  );
};

export const ListingFilter = ({
  label,
  path,
  where,
  addWhereClause,
  deleteWhereClause,
  queryParamsScope,
  disabled,
}: QuickFilterProps) => {
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);
  const { getValueFromPath } = useSchema();
  const defaultValue = getValueFromPath(path, where)?.id?._in ?? [];

  const handleChange = (value: string[]) => {
    let newWhere = deleteWhereClause(where, path, true);
    if (value.length > 0) {
      newWhere = addWhereClause(newWhere, [...path, 'id', '_in'], value);
    }
    setFiltersParams(newWhere);
  };
  const handleDelete =
    defaultValue.length > 0 ? () => handleChange([]) : undefined;

  return (
    <FilterChip
      label={label}
      icon={<Building size={16} />}
      onDelete={handleDelete}
      disabled={disabled?.(where) ?? false}
      renderFilter={({ handleClose }) => (
        <ListingMultiSelect
          onClose={handleClose}
          onChange={handleChange}
          defaultValue={defaultValue}
        />
      )}
    />
  );
};
